import {
    Add as AddIcon,
    Category as CategoryIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    Edit as EditIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { categoriesService } from '../services/api';

// Composant pour une ligne de catégorie draggable
function SortableRow({ category, handleEditCategory, handleDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'default',
  };

  return (
    <TableRow ref={setNodeRef} style={style} key={category.id}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            {...attributes}
            {...listeners}
            sx={{ 
              cursor: 'grab', 
              display: 'flex', 
              alignItems: 'center',
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <DragIcon color="action" />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {category.displayOrder || 0}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {category.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="textSecondary">
          {category.description}
        </Typography>
      </TableCell>
      <TableCell>
        {category.icon && (
          <Box
            component="img"
            src={`${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${category.icon}`}
            alt="Icône"
            sx={{
              width: 32,
              height: 32,
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={category.subcategories?.length || 0}
          color="primary"
          size="small"
        />
      </TableCell>
      <TableCell>
        <IconButton size="small" onClick={() => handleEditCategory(category)}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => handleDelete(category.id)}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Configuration des capteurs pour le drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await categoriesService.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNewCategory = () => {
    navigate('/categories/new');
  };

  const handleEditCategory = (category) => {
    navigate(`/categories/edit/${category.id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        await categoriesService.delete(id);
        fetchData();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  // Gestion du drag & drop
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredCategories.findIndex((cat) => cat.id === active.id);
      const newIndex = filteredCategories.findIndex((cat) => cat.id === over.id);

      // Réorganiser localement
      const newOrder = arrayMove(filteredCategories, oldIndex, newIndex);
      
      // Mettre à jour les displayOrder
      const updatedCategories = newOrder.map((cat, index) => ({
        ...cat,
        displayOrder: index,
      }));
      
      // Mettre à jour l'état local immédiatement pour un retour visuel instantané
      setCategories((prevCategories) => {
        const updatedAll = [...prevCategories];
        updatedCategories.forEach((updated) => {
          const idx = updatedAll.findIndex((c) => c.id === updated.id);
          if (idx !== -1) {
            updatedAll[idx] = updated;
          }
        });
        return updatedAll.sort((a, b) => a.displayOrder - b.displayOrder);
      });

      // Envoyer la mise à jour au serveur
      try {
        await categoriesService.updateOrder(
          updatedCategories.map((cat) => ({ id: cat.id, displayOrder: cat.displayOrder }))
        );
      } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'ordre:', error);
        // En cas d'erreur, recharger les données
        fetchData();
      }
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCategories = filteredCategories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return <LoadingSpinner message="Chargement des catégories..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Gestion des Catégories
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewCategory}
        >
          Nouvelle Catégorie
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher une catégorie..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ordre</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Icône</TableCell>
              <TableCell>Sous-catégories</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={paginatedCategories.map((cat) => cat.id)}
              strategy={verticalListSortingStrategy}
            >
              <TableBody>
                {paginatedCategories.map((category) => (
                  <SortableRow
                    key={category.id}
                    category={category}
                    handleEditCategory={handleEditCategory}
                    handleDelete={handleDelete}
                  />
                ))}
              </TableBody>
            </SortableContext>
          </DndContext>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCategories.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default Categories;
